import React from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Link from '@amzn/awsui-components-console/link';
import Badge from '@amzn/awsui-components-console/badge';

interface MetaBoxProps {
  cxFamilyId: string;
  cxIds: string[];
  /** When set, renders variant-specific metadata */
  variantCxId?: string;
  /** URL to navigate back to the base string (variant view only) */
  baseStringHref?: string;
  /** String group version from the API (shown in variant view only) */
  stringGroupVersion?: string | null;
  /** TRANSCOM anchor. Empty in variant view; renders as `-` when empty. */
  anchorId?: string | null;
}

/** Renders a single key-value row with label left-aligned and value right-aligned.
 *  Uses minWidth:0 on flex children to allow shrinking below content size,
 *  and overflowWrap/wordBreak on the value to wrap long unbreakable tokens
 *  (e.g. a long anchor) instead of forcing horizontal overflow. */
const MetaRow: React.FC<{
  label: string;
  children: React.ReactNode;
  'data-testid'?: string;
}> = ({ label, children, 'data-testid': testId }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: '8px',
    }}
  >
    <div style={{ flexShrink: 0 }}>
      <Box variant="awsui-key-label" fontSize="body-s">
        {label}
      </Box>
    </div>
    <div
      style={{
        minWidth: 0,
        overflowWrap: 'anywhere',
        wordBreak: 'break-all',
        textAlign: 'right',
      }}
      data-testid={testId}
    >
      <Box fontSize="body-s">{children}</Box>
    </div>
  </div>
);

const MetaBox: React.FC<MetaBoxProps> = ({
  cxFamilyId,
  cxIds,
  variantCxId,
  baseStringHref,
  stringGroupVersion,
  anchorId,
}) => {
  const isVariant = !!variantCxId;

  return (
    <Container header={<Header variant="h3">Details</Header>}>
      <SpaceBetween size="xxs">
        {isVariant && (
          <>
            <MetaRow label="Type" data-testid="meta-type-badge">
              <Badge color="blue">Variant</Badge>
            </MetaRow>
            <MetaRow label="CX ID" data-testid="meta-cxid">
              {variantCxId}
            </MetaRow>
            {baseStringHref && (
              <MetaRow label="Base String" data-testid="meta-base-string-link">
                <Link href={baseStringHref}>View base string</Link>
              </MetaRow>
            )}
            {/* Group Version: shown in variant view only. "· Latest" suffix from
                the mockup is intentionally omitted — DescribeString does not
                indicate whether the returned version is the latest without an
                additional ListStringGroupVersions call. */}
            {stringGroupVersion != null && (
              <MetaRow label="Group Version" data-testid="meta-group-version">
                v{stringGroupVersion}
              </MetaRow>
            )}
          </>
        )}
        <MetaRow label="CX Family" data-testid="meta-cx-family">
          {cxFamilyId}
        </MetaRow>
        <MetaRow label="CX IDs" data-testid="meta-cx-ids">
          {cxIds.length > 0 ? cxIds.join(', ') : '—'}
        </MetaRow>
        {/* Always rendered: an empty anchor is valid (variant view), so the row
            shows `-` instead of disappearing. */}
        <MetaRow label="Anchor" data-testid="meta-anchor">
          {anchorId?.trim() ? anchorId : '-'}
        </MetaRow>
      </SpaceBetween>
    </Container>
  );
};

export default MetaBox;
